import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import styles from './LessonPlanChangeDiff.module.css';

/**
 * Component to display proposed changes with diff view
 * Shows the new content with visual indicators and action buttons
 */
function LessonPlanChangeDiff({ 
  sectionName, 
  oldContent, 
  newContent, 
  onApprove, 
  onTryAgain, 
  onReject, 
  linesAdded, 
  linesRemoved,
  action = 'update',
  newName,
  isApplied = false,
  appliedStatus = 'applied',
  showCopyButton = false
}) {
  const [showCopySuccess, setShowCopySuccess] = useState(false);
  
  // Copy content to clipboard
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(newContent || '');
      setShowCopySuccess(true);
      setTimeout(() => setShowCopySuccess(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };
  
  // Get appropriate title based on action
  const getTitle = () => {
    switch (action) {
      case 'add':
        return `New Section: ${sectionName}`;
      case 'remove':
        return `Remove Section: ${sectionName}`;
      case 'rename':
        return `Rename Section: ${sectionName} → ${newName}`;
      case 'move':
        return `Move Section: ${sectionName}`;
      default:
        return `Proposed Changes: ${sectionName}`;
    }
  };

  return (
    <div className={`${styles.changeDiffContainer} ${isApplied ? styles.appliedContainer : ''}`}>
      <div className={styles.header}>
        <h4 className={styles.sectionName}>{getTitle()}</h4>
        <div className={styles.stats}>
          {isApplied && (
            <span className={appliedStatus === 'undone' ? styles.undoneLabel : styles.appliedLabel}>
              {appliedStatus === 'undone' ? '↺ Undone' : '✓ Applied'}
            </span>
          )}
          {linesAdded > 0 && (
            <span className={styles.added}>+{linesAdded} lines</span>
          )}
          {linesRemoved > 0 && (
            <span className={styles.removed}>-{linesRemoved} lines</span>
          )}
          {showCopyButton && (
            <button 
              className={styles.copyButton}
              onClick={handleCopy}
              title="Copy content"
            >
              {showCopySuccess ? '✓ Copied!' : '📋 Copy'}
            </button>
          )}
        </div>
      </div>

      {/* Only show content preview for actions that have content */}
      {action !== 'remove' && action !== 'rename' && action !== 'move' && newContent && (
        <div className={styles.content}>
          <div className={styles.preview}>
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {newContent}
            </ReactMarkdown>
          </div>
        </div>
      )}

      {/* For rename, show a simple confirmation message */}
      {action === 'rename' && (
        <div className={styles.confirmationMessage}>
          <p>Rename "{sectionName}" to "{newName}"</p>
        </div>
      )}

      {/* For remove, show a warning */}
      {action === 'remove' && (
        <div className={styles.warningMessage}>
          <p>⚠️ This will permanently remove the "{sectionName}" section.</p>
        </div>
      )}

      {/* For move, show position info */}
      {action === 'move' && (
        <div className={styles.confirmationMessage}>
          <p>The section will be repositioned in your lesson plan.</p>
        </div>
      )}

      {/* Only show action buttons if this is a pending change (not applied) */}
      {!isApplied && (
        <div className={styles.actions}>
          <button 
            className={`${styles.actionButton} ${styles.approve}`}
            onClick={onApprove}
            title="Apply these changes"
          >
            ✓ Approve
          </button>
          <button 
            className={`${styles.actionButton} ${styles.tryAgain}`}
            onClick={onTryAgain}
            title="Ask AI to generate a different version"
          >
            🔄 Try Again
          </button>
          <button 
            className={`${styles.actionButton} ${styles.reject}`}
            onClick={onReject}
            title="Decline these changes"
          >
            ✗ Reject
          </button>
        </div>
      )}
    </div>
  );
}

export default LessonPlanChangeDiff;

